import { useState, useEffect } from 'react';
import { useFinancialData } from '../../context/FinancialDataContext';
import { mapQBProfitAndLoss, mapQBCashFlow, mapQBBalanceSheet, mapQBProfitAndLossByClass } from '../../utils/qbMapper';
import { apiFetch } from '../../utils/apiFetch';
import { setFirmSetting } from '../../utils/firmSettings';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function QBSyncButton({ onSyncComplete }) {
  const { global2026Monthly } = useFinancialData();
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  // Initialize the selector from the last-synced month (localStorage, set by
  // the last sync / hydrated from the shared cache) so it doesn't snap back to
  // March after a sync + reload. Falls back to 3 (Jan-Mar) on first use.
  const [throughMonth, setThroughMonth] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem('bcc_qb_through_month'));
      if (stored > 0 && stored <= 12) return stored;
    } catch (e) { /* ignore */ }
    return 3;
  });

  // Note: the shared-cache HYDRATE (read) lives in QBCacheHydrator now so
  // it runs for every user, not just Adam. This component only owns the
  // sync WRITE path (Adam clicks Sync QB → fresh data flows up to the
  // qb_sync_cache table and firm_settings.plOverridesGlobal).

  async function checkConnection() {
    try {
      const res = await apiFetch('/api/qb/sync?report=status');
      const data = await res.json();
      return data.success && data.data?.connected;
    } catch {
      return false;
    }
  }

  async function handleSync() {
    setSyncing(true);
    setStatus(null);
    setMessage('Checking QuickBooks connection...');

    const connected = await checkConnection();
    if (!connected) {
      setMessage('Connecting to QuickBooks...');
      window.location.href = '/api/qb/connect';
      return;
    }

    const monthLabel = `Jan-${MONTHS[throughMonth - 1]}`;

    try {
      // Pull P&L
      setMessage(`Pulling P&L (${monthLabel} 2026)...`);
      const plRes = await apiFetch(`/api/qb/sync?report=ProfitAndLoss&year=2026&through_month=${throughMonth}`);
      const plData = await plRes.json();
      if (!plData.success) throw new Error(plData.error || 'Failed to fetch P&L');

      // Pull Balance Sheet
      setMessage('Pulling Balance Sheet...');
      const bsRes = await apiFetch(`/api/qb/sync?report=BalanceSheet&year=2026&through_month=${throughMonth}`);
      const bsData = await bsRes.json();

      // Pull Cash Flow
      setMessage('Pulling Cash Flow Statement...');
      const cfRes = await apiFetch(`/api/qb/sync?report=CashFlow&year=2026&through_month=${throughMonth}`);
      const cfData = await cfRes.json();

      // Map the P&L data into our format
      setMessage('Processing P&L data...');
      const mapped = mapQBProfitAndLoss(plData.data);

      // Log any unmapped accounts for debugging
      if (mapped.unmapped.length > 0) {
        console.warn('Unmapped QB accounts:', mapped.unmapped);
      }

      // Store the raw data for reference
      localStorage.setItem('bcc_qb_raw_pl', JSON.stringify(plData.data));
      localStorage.setItem('bcc_qb_raw_bs', JSON.stringify(bsData.data));
      localStorage.setItem('bcc_qb_raw_cf', JSON.stringify(cfData.data));

      // Store the mapped P&L data - this will be used by FinancialDataContext
      localStorage.setItem('bcc_qb_mapped_pl', JSON.stringify(mapped));
      localStorage.setItem('bcc_qb_through_month', String(throughMonth));
      localStorage.setItem('bcc_qb_last_sync', new Date().toISOString());

      // Process Cash Flow data
      setMessage('Processing Cash Flow...');
      const mappedCF = mapQBCashFlow(cfData.data);
      localStorage.setItem('bcc_qb_mapped_cf', JSON.stringify(mappedCF));

      // Process Balance Sheet data
      const mappedBS = mapQBBalanceSheet(bsData.data);
      localStorage.setItem('bcc_qb_mapped_bs', JSON.stringify(mappedBS));

      // Update the P&L actuals - start from current context data (includes projections)
      // then overwrite closed months with QB actuals
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const plToUpdate = JSON.parse(JSON.stringify(global2026Monthly));
      for (let i = 0; i < throughMonth; i++) {
        const mk = monthKeys[i];
        if (mapped.months[mk]) {
          plToUpdate.months[mk] = JSON.parse(JSON.stringify(mapped.months[mk]));
        }
      }
      localStorage.setItem('bcc_pl_edits', JSON.stringify(plToUpdate));
      // Phase 2 of the localStorage migration: also push the merged P&L
      // overrides up to firm_settings so all users see the synced data.
      setFirmSetting('plOverridesGlobal', plToUpdate);

      // ─── Per-class actuals (QB class tracking) ─────────────────────
      // QB can only summarize by ONE dimension per report, so to get
      // class x month we pull the by-class P&L once per closed month.
      // Result feeds the class P&L views: closed months show REAL QB
      // per-class numbers; projected months still ratio-derive from global.
      setMessage('Pulling per-class actuals...');
      const classActuals = { bwm: { months: {} }, bts: { months: {} }, bia: { months: {} } };
      for (let m = 1; m <= throughMonth; m++) {
        try {
          const ccRes = await apiFetch(`/api/qb/sync?report=ProfitAndLossByClass&year=2026&month=${m}`);
          const ccData = await ccRes.json();
          if (!ccData.success) continue;
          const mappedClass = mapQBProfitAndLossByClass(ccData.data);
          if (mappedClass.unmapped?.length > 0) {
            console.warn(`[QB] unmapped by-class accounts (month ${m}):`, mappedClass.unmapped);
          }
          const mk = monthKeys[m - 1];
          for (const cls of ['bwm', 'bts', 'bia']) {
            classActuals[cls].months[mk] = mappedClass[cls];
          }
        } catch (e) {
          console.warn(`[QB] by-class pull failed for month ${m}:`, e.message);
        }
      }
      localStorage.setItem('bcc_qb_class_actuals', JSON.stringify(classActuals));
      setFirmSetting('plClassActuals', classActuals);

      // Push the entire QB pull up to the shared cache so other users
      // don't have to re-sync to see the latest pull.
      try {
        await apiFetch('/api/data/qb-cache', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            raw_pl: plData.data,
            raw_bs: bsData.data,
            raw_cf: cfData.data,
            mapped_pl: mapped,
            mapped_cf: mappedCF,
            mapped_bs: mappedBS,
            through_month: throughMonth,
          }),
        });
      } catch (e) {
        console.warn('[QB] failed to push cache to API:', e);
      }

      // Update Cash Flow actuals
      const existingCFData = localStorage.getItem('bcc_cf_overrides');
      // Cash flow actuals are in the JSON file, not localStorage. Store QB data for reference.
      // The CashFlow component derives operating CF from P&L which is now updated.

      const syncSummary = [
        `P&L: ${monthLabel}`,
        `Bank Balance: $${mappedBS.totalBankBalance.toLocaleString()}`,
        `Operating CF: Jan ${mappedCF.months[0].operatingCF.toLocaleString()}, Feb ${mappedCF.months[1].operatingCF.toLocaleString()}, Mar ${mappedCF.months[2].operatingCF.toLocaleString()}`,
      ];
      console.log('QB Sync Complete:', syncSummary);

      setStatus('success');
      setMessage(`Synced ${monthLabel} 2026. Reloading...`);

      // Notify parent and reload so context picks up the new localStorage data
      if (onSyncComplete) onSyncComplete();
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  const lastSync = localStorage.getItem('bcc_qb_last_sync');
  const lastSyncDisplay = lastSync
    ? new Date(lastSync).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <div className="flex items-center gap-2">
      <select
        value={throughMonth}
        onChange={(e) => setThroughMonth(parseInt(e.target.value))}
        disabled={syncing}
        className="px-2 py-1 text-[11px] bg-white/10 text-white border border-white/20 rounded-[4px] cursor-pointer"
        title="Pull data through this month"
      >
        {MONTHS.map((m, i) => (
          <option key={i} value={i + 1} className="text-near-black">{m}</option>
        ))}
      </select>

      <button
        onClick={handleSync}
        disabled={syncing}
        className={`px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors ${
          syncing
            ? 'bg-white/10 text-white/50 cursor-wait'
            : status === 'success'
              ? 'bg-deep-teal text-white'
              : status === 'error'
                ? 'bg-rose text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/20'
        }`}
        title={lastSyncDisplay ? `Last sync: ${lastSyncDisplay}` : 'Sync data from QuickBooks'}
      >
        {syncing ? message : status === 'success' ? 'Synced!' : status === 'error' ? 'Error' : 'Sync QB'}
      </button>
      {status === 'error' && (
        <span className="text-[10px] text-rose">{message}</span>
      )}
    </div>
  );
}
